import { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Trash2, ShieldBan, ShieldCheck, Pencil } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import VendorSuggestionTable from './VendorSuggestionTable'; // new ttoo update

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { DataTable } from '@/components/common/DataTable';
import { formatDate } from '@/utils';
import { useDebounce } from '@/hooks';
import {
  useGetVendorsQuery,
  useUpdateVendorMutation,
  useDeleteVendorMutation, // new 4 update
  useCreateVendorMutation, // new 4 update
} from '@/store/api/vendorApi'; // new update

import Loader from '@/components/common/Loader';
import toast from 'react-hot-toast';

/* ===================== TYPES ===================== */

export type VendorStatus = 'Active' | 'Blocked';

export interface Vendor {
  description: string | undefined;
  mobile: string;
  id: number;
  name: string;
  category: 'Legal' | 'Compliance' | 'Valuation' | 'Audit' | 'Trustee';
  email: string;
  phone?: string;
  website?: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  status: VendorStatus;
}

/* ===================== STATUS BADGE ===================== */

function VendorStatusBadge({ status }: { status: VendorStatus }) {
  const config: Record<VendorStatus, any> = {
    Active: {
      label: 'Active',
      container: 'bg-green-100 text-green-800',
      dot: 'bg-green-500',
    },
    Blocked: {
      label: 'Disabled',
      container: 'bg-red-100 text-red-800',
      dot: 'bg-red-500',
    },
  };

  const { label, container, dot } = config[status];

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${container}`}>
      <span className={`mr-1 h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

/* ===================== ACTIONS ===================== */

function VendorActions({ vendor }: { vendor: Vendor }) {
  const [updateVendor] = useUpdateVendorMutation(); // new update
  const [deleteVendor] = useDeleteVendorMutation(); // new 4 update
  const navigate = useNavigate();
  const handleUpdate = async (data: { isActive?: boolean; isDeleted?: boolean }) => {
    await updateVendor({
      vendorId: vendor.id,
      ...data,
    });
  };

  // from new 4 update
  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${vendor.name}?`)) {
      try {
        await deleteVendor(vendor.id).unwrap();
      } catch (error: any) {
        toast.error(error?.data?.message);
        console.error('Update failed', error);
      }
    }
  };
  // to new 4 update

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {/* /vendors/edit */}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(`/vendors/edit/${vendor?.id}`)}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>

        {vendor.status !== 'Active' && (
          <DropdownMenuItem onClick={() => handleUpdate({ isActive: true })}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Activate
          </DropdownMenuItem>
        )}

        {vendor.status === 'Active' && (
          <DropdownMenuItem onClick={() => handleUpdate({ isActive: false })}>
            <ShieldBan className="mr-2 h-4 w-4" />
            Disable
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-destructive"
          onClick={handleDelete} // new 4 update
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ===================== PAGE ===================== */

export function LegalComplianceVendorsPage() {
  const navigate = useNavigate();
  const location = useLocation(); // new ttoo update

  const [open, setOpen] = useState(false);
  const [createVendor, { isLoading: isAdding }] = useCreateVendorMutation();

  // Form State
  const [formData, setFormData] = useState({
    vendorName: '',
    category: 'Legal',
    description: '',
    website: '',
    email: '',
    mobile: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createVendor({
        vendorName: formData.vendorName,
        category: formData.category as 'Legal' | 'Compliance' | 'Valuation' | 'Audit' | 'Trustee',
        description: formData.description,
        website: formData.website,
        email: formData.email,
        phone: formData.mobile,
      }).unwrap();

      setOpen(false);
      setFormData({
        vendorName: '',
        category: 'Legal',
        description: '',
        website: '',
        email: '',
        mobile: '',
      });
    } catch (err) {
      console.error('Failed to add vendor', err);
    }
  };

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(); // new update
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(); // new update
  const debouncedSearch = useDebounce(search, 300);
  const [activeTab, setActiveTab] = useState<'VENDORS' | 'SUGGESTIONS'>('VENDORS'); // new too update

  // from new ttoo update
  useEffect(() => {
    if (location.state?.openTab === 'SUGGESTIONS') {
      setActiveTab('SUGGESTIONS');
      window.history.replaceState({}, document.title);
    } else {
      setActiveTab('VENDORS');
    }
  }, [location.state]);
  // to new ttoo update

  const { data, isLoading, isError } = useGetVendorsQuery(
    {
      skip: pageIndex * pageSize,
      take: pageSize,
      search: debouncedSearch,
      status: statusFilter, // new update
      category: categoryFilter, // new update
    },
    {
      pollingInterval: 180000,
      refetchOnFocus: true, // ✅ 3 minutes (in ms)
    }
  );

  const vendors: Vendor[] =
    data?.data?.map((v: any) => ({
      ...v,
      status: v.isActive ? 'Active' : 'Blocked',
    })) ?? [];

  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<Vendor>[] = [
    {
      header: 'Vendor Name',
      cell: ({ row }) => <span className="font-semibold">{row.original.name}</span>,
    },
    {
      id: 'category', // new update
      header: 'Category',
      accessorKey: 'category', // new udpate
      cell: ({ row }) => <span>{row.original.category}</span>,
      // form new update
      filterFn: (row, id, value) => {
        if (!value || value === 'ALL') return true;
        return String(row.getValue(id)).toLowerCase() === String(value).toLowerCase();
      },
      // to new update
    },
    {
      header: 'Email',
      cell: ({ row }) => (
        <div className="max-w-[150px] break-all">
          <a href={`mailto:${row.original.email}`} className="text-primary underline">
            {row.original.email}
          </a>
        </div>
      ),
    },
    // from new update
    {
      header: 'Mobile',
      cell: ({ row }) => <span className="whitespace-nowrap">{row.original.mobile || '—'}</span>, // new update
    },
    {
      header: 'Website',
      cell: ({ row }) => (
        <div className="min-w-[80px]">
          {row.original.website ? (
            <a href={row.original.website} target="_blank" className="text-primary underline">
              Visit
            </a>
          ) : (
            '—'
          )}
        </div>
      ),
    },
    {
      header: 'Description',
      cell: ({ row }) => (
        // new update div text change by span
        <div
          className="max-w-[200px] whitespace-normal break-words text-sm line-clamp-2"
          title={row.original.description}
        >
          {row.original.description || '—'}
        </div>
      ),
    },
    // from new update
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => <VendorStatusBadge status={row.original.status} />,
      filterFn: (row, id, value) => {
        if (!value || value === 'ALL') return true;
        return String(row.getValue(id)) === String(value);
      },
    },
    // to new update
    {
      header: 'Created',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <VendorActions vendor={row.original} />,
      enableSorting: false,
    },
  ];

  if (isLoading) return <Loader />;
  if (isError) return <div className="p-6 text-red-500">Failed to load vendors</div>;

  return (
    <div className="space-y-8 animate-fade-in pb-10">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight text-white">Legal & Compliance Vendors</h2>

        {/* form new too update */}
        <div className="flex bg-slate-900/50 border border-slate-800 rounded-lg p-1 gap-2">
          <Button
            onClick={() => navigate('/vendors/create')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Add Vendor
          </Button>
        </div>
        {/* to new too update */}
      </div>

      {/* form ne too update */}
      <div className="flex gap-1 rounded-lg bg-slate-900/50 border border-slate-800 p-1 w-fit">
        <button
          onClick={() => setActiveTab('VENDORS')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition
      ${
        activeTab === 'VENDORS'
          ? 'bg-slate-700 text-white shadow'
          : 'text-slate-400 hover:text-white'
      }
    `}
        >
          Vendor List
        </button>

        <button
          onClick={() => setActiveTab('SUGGESTIONS')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition
      ${
        activeTab === 'SUGGESTIONS'
          ? 'bg-slate-700 text-white shadow'
          : 'text-slate-400 hover:text-white'
      }
    `}
        >
          Suggestion List
        </button>
      </div>
      {/* to new too update */}

      {/* from new ttoo update */}
      {/* from new ttoo update */}
      {activeTab === 'VENDORS' && (
        <div className="rounded-xl bg-slate-900/50 shadow-sm overflow-hidden px-1">
          <DataTable
            data={vendors}
            columns={columns}
            enableCategoryFilter={true}
            onCategoryFilterChange={(value) => {
              setCategoryFilter(value === 'ALL' ? undefined : value);
              setPageIndex(0);
            }}
            onVendorStatusFilterChange={(value) => {
              setStatusFilter(value === 'ALL' ? undefined : value);
              setPageIndex(0);
            }}
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={Math.ceil(totalCount / pageSize)}
            manualPagination
            onPaginationChange={(updater) => {
              const next =
                typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
              setPageIndex(next.pageIndex);
              setPageSize(next.pageSize);
            }}
            onSearchChange={(value) => {
              setSearch(value);
              setPageIndex(0);
            }}
          />
        </div>
      )}

      {activeTab === 'SUGGESTIONS' && <VendorSuggestionTable />}
      {/* to new ttoo update */}
      {/* to new ttoo update */}
    </div>
  );
}
